import * as THREE from "three";

export interface MeshDataStats {
  numVerts: number;
  numTris: number;
  memoryBytes: number;
}

export function meshDataStatsNew(): MeshDataStats {
  return {
    numVerts: 0,
    numTris: 0,
    memoryBytes: 0,
  };
}

export function dataStatsFromThreeMesh(mesh: THREE.Mesh): MeshDataStats {
  if (!mesh.geometry) {
    return { numVerts: 0, numTris: 0, memoryBytes: 0 };
  }
  let memoryBytes = 0;
  const BYTES_PER_FLOAT16 = 2;
  const BYTES_PER_FLOAT32 = 4;
  for (const attr of Object.values((mesh.geometry as THREE.BufferGeometry).attributes)) {
    if (attr instanceof THREE.Float16BufferAttribute) {
      memoryBytes += attr.count * attr.itemSize * BYTES_PER_FLOAT16;
    } else {
      memoryBytes += attr.count * attr.itemSize * BYTES_PER_FLOAT32;
    }
  }

  const numVerts = (mesh.geometry as THREE.BufferGeometry).attributes.position.count;

  // Assume that we need 32-bit indices if the vertex count is high enough.
  const bytesPerIndex = numVerts > 65535 ? 4 : 2;

  let numTris;
  const index: THREE.BufferAttribute | null = (mesh.geometry as THREE.BufferGeometry).index;
  if (index) {
    memoryBytes += index.count * bytesPerIndex;
    numTris = index.count / 3;
  } else {
    numTris = (mesh.geometry as THREE.BufferGeometry).getAttribute("position").count / 3;
  }

  return {
    numVerts,
    numTris,
    memoryBytes,
  };
}

export function dataStatsFromThreeObjectHierarchy(object: THREE.Object3D): MeshDataStats {
  let stats = meshDataStatsNew();
  const geomCounted = new Set<THREE.BufferGeometry>();
  object.traverse((node) => {
    if (node instanceof THREE.Mesh) {
      if (!node.geometry || geomCounted.has(node.geometry)) {
        return;
      }
      geomCounted.add(node.geometry);
      stats = meshDataStatsSum(stats, dataStatsFromThreeMesh(node));
    }
  });
  return stats;
}

export function meshDataStatsSum(statsA: MeshDataStats, statsB: MeshDataStats): MeshDataStats {
  const stats: MeshDataStats = {
    numVerts: statsA.numVerts + statsB.numVerts,
    numTris: statsA.numTris + statsB.numTris,
    memoryBytes: statsA.memoryBytes + statsB.memoryBytes,
  };
  return stats;
}

export function meshDataStatsSubtract(statsA: MeshDataStats, statsB: MeshDataStats): MeshDataStats {
  const stats: MeshDataStats = {
    numVerts: statsA.numVerts - statsB.numVerts,
    numTris: statsA.numTris - statsB.numTris,
    memoryBytes: statsA.memoryBytes - statsB.memoryBytes,
  };
  return stats;
}

export function formatBytes(numBytes: number): string {
  let rounded = numBytes;
  const suffixes = ["B", "kB", "MB", "GB", "TB"];
  let suffixIndex = 0;
  while (rounded > 1000 && suffixIndex + 1 < suffixes.length) {
    rounded = rounded / 1000;
    suffixIndex += 1;
  }
  if (suffixIndex === 0) {
    return rounded + " B";
  }
  return rounded.toFixed(1) + " " + suffixes[suffixIndex];
}
